import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { Spinner } from 'equasion-lib';
import ApiError from '@/lib/shared-coms/ApiError';
import { useRootStore } from '@/lib/providers/RootProvider';
import PickPathSetup from './PickPathSetup';

// Zones hang off SHIPPING locations (the admin Zones page creates them there), so those are
// the floors a walk can be captured for.
export const PICK_PATH_LOCATIONS = gql`
  query PickPathLocations($companyId: ID!) {
    locations(companyId: $companyId, type: "shipping") {
      id
      companyName
      address {
        lineOne
        city
        state
      }
    }
  }
`;

// The whole floor, unpaginated: a walk has to know which zones it has NOT reached yet, and
// the largest real floor to date is ~3,200 zones — a few hundred KB, once, on a setup screen.
export const PICK_PATH_ZONES = gql`
  query PickPathZones($companyId: ID!, $locationId: ID!) {
    zones(companyId: $companyId, locationId: $locationId) {
      id
      name
      zoneType
      area
      rack
      shelf
      bin
      pickSequence
    }
  }
`;

export const ZONE_PICK_PATH_SET = gql`
  mutation ZonePickPathSet($companyId: ID!, $locationId: ID!, $zoneIds: [ID!]!) {
    zonePickPathSet(companyId: $companyId, locationId: $locationId, zoneIds: $zoneIds) {
      id
      name
      pickSequence
    }
  }
`;

function PickPathSetupData(props) {
  const companyId = useRootStore((state) => state.selectedCompany.id);
  const getPermission = useRootStore((state) => state.getPermission) as (
    permission: string
  ) => unknown;
  // Same gate as the mutation's @auth: a picker without it can still SEE the walk (it is
  // the order they will be sent in), they just cannot rewrite it.
  const canEdit = !!getPermission('zones:update');
  const [locationId, setLocationId] = useState<string | null>(null);

  const {
    data: locationsData,
    loading: locationsLoading,
    error: locationsError
  } = useQuery(PICK_PATH_LOCATIONS, {
    fetchPolicy: 'network-only',
    variables: { companyId }
  });
  const locations = locationsData?.locations || [];

  // One floor means no question to ask.
  useEffect(() => {
    if (!locationId && locations.length === 1) setLocationId(locations[0].id);
  }, [locations, locationId]);

  const {
    data: zonesData,
    loading: zonesLoading,
    error: zonesError,
    refetch: refetchZones
  } = useQuery(PICK_PATH_ZONES, {
    fetchPolicy: 'network-only',
    variables: { companyId, locationId },
    skip: !locationId
  });

  const [zonePickPathSet, { loading: saving }] = useMutation(ZONE_PICK_PATH_SET);

  if (locationsLoading) return <Spinner />;
  if (locationsError) return <ApiError error={locationsError} />;
  if (zonesError) return <ApiError error={zonesError} />;

  return (
    <PickPathSetup
      companyId={companyId}
      canEdit={canEdit}
      locations={locations}
      locationId={locationId}
      setLocationId={setLocationId}
      zones={zonesData?.zones || []}
      zonesLoading={zonesLoading}
      refetchZones={refetchZones}
      zonePickPathSet={zonePickPathSet}
      saving={saving}
      {...props}
    />
  );
}

export default PickPathSetupData;
